import React from "react";

import { Flex } from "src/components/Flex.tsx";
import type {
  GridFilterModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";

import { Panel } from "src/components/Panel/Panel.tsx";

import { PanelFace } from "src/components/Form/panels/TransferPanel/PanelFace.tsx";

import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import type {
  CustomerGroupResource,
  CustomerResource,
  DeliveryMethodResource,
  DeliveryServiceResource,
  DiscountResource,
  ManufacturerResource,
  PaymentMethodResource,
  VoucherSerieResource,
} from "@exonn/js-sdk";
import { useResourceFormData } from "src/components/Form/hooks/useResourceFormData.ts";
import { formatDefinitionAttributes } from "src/admin/modules/variant/utils/formatAttributeValues.ts";
import type { ProductItemResource } from "@exonn/js-sdk/src/namespaces/system/schemas";
import { useResourceFormContext } from "src/components/Form/hooks/useResourceFormContext.ts";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";

export type ValidRowModel = GridValidRowModel & {
  id: string;
};

await loadTranslations(TranslationNamespaceEnum.TransferPanel);

export type TransferPanelManageData = {
  action: "assign" | "unassign" | "unassignAll" | "assignAll";
  rows?: any[];
  is_excluded?: 0 | 1;
  locale: string;
  filter?: GridFilterModel;
};

export type TransferPanelGetDataParams = {
  is_assigned: 0 | 1;
  is_excluded: 0 | 1;
  filter?: GridFilterModel;
  page: {
    size: number;
    number: number;
  };
};

export type TransferPanelProps = {
  resource: string;
};

export function TransferPanel({ resource }: TransferPanelProps) {
  const { t } = useTranslation(TranslationNamespaceEnum.TransferPanel);
  const formData = useResourceFormData();
  const formContext = useResourceFormContext();

  const formResource = formContext.resource;
  const include = getIncludedProps({
    resource,
    formResource,
    formData,
    action: formContext.action,
  });
  const exclude = getExcludedProps({
    resource,
    formResource,
    formData,
    action: formContext.action,
  });
  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>{t(`${resource}.title`)}</Panel.Title>
      </Panel.Header>
      <Panel.Content>
        <Flex gap={20} flexDirection={"column"}>
          {include ? (
            <PanelFace
              formResource={formResource}
              resource={resource}
              rowsCount={include.rowsCount}
              rows={include.rows}
              isExcluded={false}
            />
          ) : null}
          {exclude ? (
            <PanelFace
              formResource={formResource}
              resource={resource}
              rowsCount={exclude.rowsCount}
              rows={exclude.rows}
              isExcluded={true}
            />
          ) : null}
        </Flex>
      </Panel.Content>
    </Panel>
  );
}

const getIncludedProps = ({
  resource: transferResource,
  formResource: mainResource,
  formData,
  action,
}: {
  resource: string;
  formResource: string;
  formData: any;
  action: "create" | "edit";
}) => {
  if (action === "create") {
    return {
      rows: [],
      rowsCount: 0,
    };
  }
  if (
    mainResource === ResourceEnum.Manufacturer &&
    transferResource === ResourceEnum.Variant
  ) {
    const resource = formData?.data as ManufacturerResource | undefined;
    return {
      rowsCount: resource?.products_count ?? 0,
      rows: resource?.products ?? [],
    };
  }
  if (
    mainResource === ResourceEnum.CustomerGroup &&
    transferResource === ResourceEnum.Customer
  ) {
    const resource = formData?.data as CustomerGroupResource | undefined;
    return {
      rowsCount: resource?.customers_count ?? 0,
      rows: (resource?.customers ?? []).map((item) => ({
        id: item.id,
        title: item.first_name + " " + item.last_name,
      })),
    };
  }
  if (
    mainResource === ResourceEnum.Customer &&
    transferResource === ResourceEnum.CustomerGroup
  ) {
    const resource = formData?.data as CustomerResource | undefined;
    return {
      rowsCount: resource?.customerGroupsCount ?? 0,
      rows: resource?.customerGroups ?? [],
    };
  }
  if (
    mainResource === ResourceEnum.PaymentMethod ||
    mainResource === ResourceEnum.VoucherSerie ||
    mainResource === ResourceEnum.Discount ||
    mainResource === ResourceEnum.DeliveryService
  ) {
    const resource = formData?.data as
      | PaymentMethodResource
      | VoucherSerieResource
      | DiscountResource
      | DeliveryServiceResource
      | undefined;
    if (transferResource === ResourceEnum.ProductGroup) {
      return {
        rowsCount: resource?.included_product_groups_count ?? 0,
        rows: resource?.included_product_groups ?? [],
      };
    }
    if (transferResource === ResourceEnum.Country) {
      return {
        rowsCount: resource?.included_countries_count ?? 0,
        rows: (resource?.included_countries ?? []).map((item) => ({
          id: item.id,
          title: item.name,
        })),
      };
    }
    if (transferResource === ResourceEnum.Variant) {
      return {
        rowsCount: resource?.included_product_items_count ?? 0,
        rows: (resource?.included_product_items ?? []).map((item) => ({
          id: item.id,
          title: formatProductItemTitle(item),
        })),
      };
    }
    if (transferResource === ResourceEnum.CustomerGroup) {
      return {
        rowsCount: resource?.included_customer_groups_count ?? 0,
        rows: resource?.included_customer_groups ?? [],
      };
    }
    if (transferResource === ResourceEnum.Customer) {
      return {
        rowsCount: resource?.included_customers_count ?? 0,
        rows: (resource?.included_customers ?? []).map((item) => ({
          id: item.id,
          title: item.first_name + " " + item.last_name,
        })),
      };
    }
  }
  if (
    transferResource === ResourceEnum.DeliveryService &&
    mainResource === ResourceEnum.DeliveryMethod
  ) {
    const resource = formData?.data as DeliveryMethodResource | undefined;
    return {
      rowsCount: resource?.delivery_services_count ?? 0,
      rows: (resource?.delivery_services ?? []).map((item) => ({
        id: item.id,
        title: item.title,
      })),
    };
  }

  throw new Error(
    `getIncludedProps is not implemented for [${mainResource}] resource`,
  );
};

const getExcludedProps = ({
  resource: transferResource,
  formResource: mainResource,
  formData,
  action,
}: {
  resource: string;
  formResource: string;
  formData: any;
  action: "create" | "edit";
}) => {
  if (
    mainResource === ResourceEnum.Manufacturer &&
    transferResource === ResourceEnum.Variant
  ) {
    return null;
  }

  if (
    mainResource === ResourceEnum.CustomerGroup &&
    transferResource === ResourceEnum.Customer
  ) {
    return null;
  }
  if (
    mainResource === ResourceEnum.Customer &&
    transferResource === ResourceEnum.CustomerGroup
  ) {
    return null;
  }
  if (
    mainResource === ResourceEnum.DeliveryMethod &&
    transferResource === ResourceEnum.DeliveryService
  ) {
    return null;
  }
  if (action === "create") {
    return {
      rows: [],
      rowsCount: 0,
    };
  }
  if (
    mainResource === ResourceEnum.PaymentMethod ||
    mainResource === ResourceEnum.VoucherSerie ||
    mainResource === ResourceEnum.Discount ||
    mainResource === ResourceEnum.DeliveryService
  ) {
    const resource = formData?.data as
      | PaymentMethodResource
      | VoucherSerieResource
      | DiscountResource
      | DeliveryServiceResource
      | undefined;
    if (transferResource === ResourceEnum.ProductGroup) {
      return {
        rowsCount: resource?.excluded_product_groups_count ?? 0,
        rows: resource?.excluded_product_groups ?? [],
      };
    }
    if (transferResource === ResourceEnum.Country) {
      return {
        rowsCount: resource?.excluded_countries_count ?? 0,
        rows: (resource?.excluded_countries ?? []).map((item) => ({
          id: item.id,
          title: item.name,
        })),
      };
    }
    if (transferResource === ResourceEnum.Variant) {
      return {
        rowsCount: resource?.excluded_product_items_count ?? 0,
        rows: (resource?.excluded_product_items ?? []).map((item) => ({
          id: item.id,
          title: formatProductItemTitle(item),
        })),
      };
    }

    if (transferResource === ResourceEnum.CustomerGroup) {
      return {
        rowsCount: resource?.excluded_customer_groups_count ?? 0,
        rows: resource?.excluded_customer_groups ?? [],
      };
    }

    if (transferResource === ResourceEnum.Customer) {
      return {
        rowsCount: resource?.excluded_customers_count ?? 0,
        rows: (resource?.excluded_customers ?? []).map((item) => ({
          id: item.id,
          title: item.first_name + " " + item.last_name,
        })),
      };
    }
  }

  throw new Error(
    `getExcludedProps is not implemented for [${mainResource}] and [${transferResource}] resource`,
  );
};

const formatProductItemTitle = (item: ProductItemResource) => {
  const attributeValues = item?.definitionAttributeValues || [];
  const attributeValuesPost =
    attributeValues.length > 0
      ? ` (${formatDefinitionAttributes(attributeValues)})`
      : "";
  return (
    (item.title || item?.parentProductItem?.title || "") + attributeValuesPost
  );
};
